import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../Redux/hooks';
import { AuthService } from '../../../Services/AuthService';
import { useTranslation } from '../../../i18n/useTranslation';
import type { TranslationKey } from '../../../i18n/he';
import { routes } from '../../../Utils/routes';
import logo from '../../../Assets/Images/logo.png';
import { Menu } from '../Menu/Menu';
import { NavDrawer } from '../NavDrawer/NavDrawer';
import './AppHeader.css';

/**
 * Order matters: the match is a prefix test, so a longer path has to be listed
 * before any shorter one it starts with. The dashboard is last because every
 * path starts with it, and it is matched exactly rather than by prefix.
 */
const TITLES: readonly [string, TranslationKey][] = [
  [routes.googleCalendar, 'navGoogleCalendar'],
  [routes.paymentDocuments, 'navPaymentDocuments'],
  [routes.receiptReports, 'navReceiptReports'],
  [routes.business, 'businessTitle'],
  [routes.admin, 'navAdmin'],
  [routes.students, 'navStudents'],
  [routes.calendar, 'navCalendar'],
  [routes.payments, 'navPayments'],
  [routes.lessons, 'navLessons'],
  [routes.receipts, 'navReceipts'],
  [routes.reminders, 'navReminders'],
  [routes.reports, 'navReports'],
  [routes.backup, 'navBackup'],
  [routes.premiumArea, 'navPremium'],
  [routes.profile, 'navProfile'],
  [routes.dashboard, 'navDashboard']
];

export function AppHeader() {
  const { tr } = useTranslation();
  const location = useLocation();
  const user = useAppSelector(state => state.session.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const title = tr(TITLES.find(([path]) => path === routes.dashboard
    ? location.pathname === path
    : location.pathname.startsWith(path))?.[1] ?? 'navDashboard');
  const initial = (user?.name ?? user?.email ?? 'M').trim().charAt(0).toUpperCase();

  return (
    <header className="authenticated-header">
      <button
        className="app-header-button app-menu-trigger"
        type="button"
        onClick={() => setDrawerOpen(true)}
        aria-label={tr('openMenu')}
        aria-haspopup="dialog"
        aria-expanded={drawerOpen}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
      </button>

      <Link className="app-brand" to={routes.dashboard} aria-label={tr('appName')}>
        <img src={logo} alt="" />
        <span className="app-brand-copy">
          <strong>{tr('appName')}</strong>
          <small>{tr('appSubtitle')}</small>
        </span>
      </Link>

      <h1 className="app-view-title" aria-label={title} data-title={title} />

      <div className="app-header-controls">
        <Link className="app-user-chip" to={routes.profile} title={user?.name ?? user?.email ?? ''}>
          <span className="app-user-avatar" aria-hidden="true">{initial}</span>
          <span>{user?.name ?? user?.email}</span>
        </Link>
        <button
          className="app-header-button app-signout"
          type="button"
          onClick={() => void AuthService.signOut()}
          aria-label={tr('navLogout')}
          title={tr('navLogout')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 12H3m0 0 4-4m-4 4 4 4M14 4h5a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5" /></svg>
        </button>
      </div>
      <Menu />
      <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  );
}
